// Browser-side RedGifs lookup. Our /api proxies can get rate-limited by RedGifs (429) when
// Cloudflare's shared IPs have asked too often. When that happens we work the media URLs out
// for the poster images here instead — no login needed:
//   1. links already pointing at media.redgifs.com/<Name>.mp4 give the name directly;
//   2. otherwise RedGifs' public oEmbed endpoint (open to any site) returns the poster URL,
//      and the video sits next to it at the same name.

export interface RedGifsMedia { poster: string | null; hd: string | null; sd: string | null }

const MEDIA_PREFIX = 'rg-media:';
const MEDIA_MS = 7 * 24 * 60 * 60 * 1000;

// Once the proxy has failed this session, skip straight to the direct route
let proxyBlocked = false;
export const isProxyBlocked = () => proxyBlocked;
export const markProxyBlocked = () => { proxyBlocked = true; };

const read = <T,>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { value, expires } = JSON.parse(raw) as { value: T; expires: number };
    return Date.now() < expires ? value : null;
  } catch { return null; }
};
const write = (key: string, value: unknown, ttl: number) => {
  try { localStorage.setItem(key, JSON.stringify({ value, expires: Date.now() + ttl })); } catch { /* storage full or blocked */ }
};

const fromName = (name: string): RedGifsMedia => ({
  poster: `https://media.redgifs.com/${name}-poster.jpg`,
  hd: `https://media.redgifs.com/${name}.mp4`,
  sd: `https://media.redgifs.com/${name}-mobile.mp4`,
});

// "https://media.redgifs.com/GainsboroAnguishedBoa.mp4" → "GainsboroAnguishedBoa"
const mediaName = (url: string | null | undefined) => url?.match(/media\.redgifs\.com\/([A-Za-z]+)(?:-[a-z]+)?\.(?:mp4|jpg)/)?.[1] ?? null;

export const cachedRedGifs = (id: string, url?: string | null): RedGifsMedia | null => {
  const name = mediaName(url);
  return name ? fromName(name) : read<RedGifsMedia>(MEDIA_PREFIX + id.toLowerCase());
};

const inFlight = new Map<string, Promise<RedGifsMedia | null>>();

// At most 4 lookups at once, so a page of thumbnails doesn't trip RedGifs' rate limit
let active = 0;
const waiting: (() => void)[] = [];
const slot = async () => {
  if (active >= 4) await new Promise<void>(resolve => waiting.push(resolve));
  active++;
};
const release = () => { active--; waiting.shift()?.(); };

export const resolveRedGifs = (id: string, url?: string | null): Promise<RedGifsMedia | null> => {
  const key = id.toLowerCase();
  const known = cachedRedGifs(key, url);
  if (known) return Promise.resolve(known);
  if (inFlight.has(key)) return inFlight.get(key)!;
  const request = (async () => {
    await slot();
    try {
      const res = await fetch(`https://api.redgifs.com/v1/oembed?url=${encodeURIComponent(`https://www.redgifs.com/watch/${key}`)}`);
      if (!res.ok) return null;
      const { thumbnail_url } = await res.json() as { thumbnail_url?: string };
      const name = mediaName(thumbnail_url);
      if (!name) return null;
      const media = fromName(name);
      write(MEDIA_PREFIX + key, media, MEDIA_MS);
      return media;
    } catch { return null; }
    finally { inFlight.delete(key); release(); }
  })();
  inFlight.set(key, request);
  return request;
};
